import {
	Box,
	IconButton,
	InputAdornment,
	TextField,
	Tooltip,
} from "@mui/material";
import type { BaseTextFieldProps, OutlinedInputProps } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import React, { useEffect, useState } from "react";
import type { RegisterOptions, UseFormRegister } from "react-hook-form";
import { imgUrl } from "src/Helpers/utils/constants";

export interface CommonFileUploaderProps extends BaseTextFieldProps {
	label: string;
	name?: string;
	accept?: string;
	required?: boolean;
	existingUrl?: string | null;
	selectedFile?: File | null;
	onFileChange?: (file: File | null) => void;
	register?: UseFormRegister<any>;
	rules?: RegisterOptions;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	inputRef?: React.Ref<any>;
	InputProps?: Partial<OutlinedInputProps>;
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const CommonFileUploader = React.forwardRef<
	HTMLInputElement,
	CommonFileUploaderProps
>(
	(
		{
			label,
			name,
			accept,
			required,
			existingUrl,
			selectedFile,
			onFileChange,
			register,
			rules,
			onChange,
			onBlur,
			inputRef: customInputRef,
			helperText,
			fullWidth = true,
			variant = "outlined",
			InputProps,
			...restProps
		},
		ref
	) => {
		const existingIsPdf = existingUrl
			? existingUrl.toLowerCase().endsWith(".pdf")
			: false;

		const [isPdf, setIsPdf] = useState<boolean>(existingIsPdf);
		const [previewUrl, setPreviewUrl] = useState<string | null>(null);

		useEffect(() => {
			setIsPdf(existingIsPdf);
			setPreviewUrl(null);
		}, [existingUrl, existingIsPdf]);

		// Handle external selectedFile change if provided
		useEffect(() => {
			if (selectedFile) {
				if (selectedFile.type.startsWith("image/")) {
					const url = URL.createObjectURL(selectedFile);
					setPreviewUrl(url);
					setIsPdf(false);
					return () => URL.revokeObjectURL(url);
				} else if (
					selectedFile.type === "application/pdf" ||
					selectedFile.name.toLowerCase().endsWith(".pdf")
				) {
					setPreviewUrl(null);
					setIsPdf(true);
				}
			}
		}, [selectedFile]);

		const existingFullUrl = existingUrl
			? existingUrl.startsWith("http") || existingUrl.startsWith("blob:")
				? existingUrl
				: `${imgUrl}/${existingUrl}`
			: null;

		const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0] || null;
			if (file) {
				if (file.type.startsWith("image/")) {
					const url = URL.createObjectURL(file);
					setPreviewUrl(url);
					setIsPdf(false);
				} else if (
					file.type === "application/pdf" ||
					file.name.toLowerCase().endsWith(".pdf")
				) {
					setPreviewUrl(null);
					setIsPdf(true);
				} else {
					setPreviewUrl(null);
					setIsPdf(false);
				}
			} else {
				setPreviewUrl(null);
				setIsPdf(existingIsPdf);
			}

			onFileChange?.(file);
		};

		// Support both register={register} and {...register("name")}
		const registered =
			register && name ? register(name, rules) : undefined;

		const combinedRef = ref || registered?.ref || customInputRef;
		const combinedName = registered?.name || name;

		const combinedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			registered?.onChange(e);
			onChange?.(e);
			handleLocalChange(e);
		};

		const combinedBlur = (e: React.FocusEvent<HTMLInputElement>) => {
			registered?.onBlur(e);
			onBlur?.(e);
		};

		const activeUrl = previewUrl || existingFullUrl;

		return (
			<TextField
				variant={variant as any}
				fullWidth={fullWidth}
				type='file'
				label={label}
				name={combinedName}
				InputLabelProps={{ shrink: true }}
				inputProps={{ accept, ...restProps.inputProps }}
				inputRef={combinedRef}
				onChange={combinedChange}
				onBlur={combinedBlur}
				required={required && !existingUrl && !selectedFile}
				helperText={helperText}
				InputProps={{
					...InputProps,
					endAdornment: (
						<>
							{InputProps?.endAdornment}
							{activeUrl && (
								<InputAdornment position='end' sx={{ mr: -0.5 }}>
									{isPdf ? (
										<Tooltip title='Click to view file' arrow>
											<IconButton
												component='a'
												href={activeUrl}
												target='_blank'
												rel='noopener noreferrer'
												size='small'
												color='error'
												sx={{ p: 0.5 }}
											>
												<PictureAsPdfOutlinedIcon fontSize='medium' />
											</IconButton>
										</Tooltip>
									) : (
										<Tooltip title='Click to view full image' arrow>
											<Box
												component='a'
												href={activeUrl}
												target='_blank'
												rel='noopener noreferrer'
												sx={{
													display: "inline-flex",
													alignItems: "center",
													cursor: "pointer",
													borderRadius: 1,
													overflow: "hidden",
													border: "1px solid",
													borderColor: "divider",
													transition: "transform 0.15s ease-in-out",
													"&:hover": {
														transform: "scale(1.08)",
														borderColor: "primary.main",
													},
												}}
											>
												<Box
													component='img'
													src={activeUrl}
													alt='Preview'
													sx={{
														width: 34,
														height: 34,
														objectFit: "cover",
														display: "block",
													}}
													onError={(e: any) => {
														e.target.onerror = null;
														e.target.src =
															"/static/images/avatars/avatar2.png";
													}}
												/>
											</Box>
										</Tooltip>
									)}
								</InputAdornment>
							)}
						</>
					),
				}}
				{...restProps}
			/>
		);
	}
);

CommonFileUploader.displayName = "CommonFileUploader";

export default CommonFileUploader;
